import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useEffect } from 'react';
import { Stack } from '@hub/stack';
import { H3 } from '@hub/heading';
import { Text } from '@hub/text';
import { Link, LinkOverlay } from '@hub/link';
import { Grid } from '@hub/grid';
import { DEFAULT_BREAKPOINT, useBreakpoint, useToken, forwardRef } from '@hub/design-system-base';
import { CoreContainer } from '@hub/core-container';
import trackClickEvents, { categories, labels } from '@scentregroup/shared/hub-components/analytics/trackEvents';
import Paragraph from "@scentregroup/ui/components/paragraph";
import Markdown from '../../components/markdown/index';
import { CloudinaryImage, IMAGE_SIZES_COLOR_CARD_HERO } from '../cloudinary-image';
import { useSupportingColors } from '../../hooks/use-supporting-colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ColorCardContent = _ref => {
  let {
    isFullWidth,
    title,
    shortDescription,
    callToAction,
    supplementaryText,
    supportingColor,
    position,
    index,
    tag,
    clickEvent
  } = _ref;
  const {
    supportingForegroundColor: primaryForegroundColor
  } = useSupportingColors(supportingColor);
  const {
    supportingForegroundColor: secondaryForegroundColor
  } = useSupportingColors(supportingColor, {
    lightForegroundColor: 'manatee',
    darkForegroundColor: 'quartz'
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Stack, {
      sx: _objectSpread(_objectSpread({}, isFullWidth ? {
        width: '70%',
        marginX: 'auto'
      } : {}), {}, {
        color: primaryForegroundColor
      }),
      "data-id": "color-cards_index_ColorCardContent_Stack",
      children: [/*#__PURE__*/_jsx(Text, {
        lineHeight: "base",
        noOfLines: 2,
        tone: "strong",
        "data-id": "color-cards_index_ColorCardContent_Stack_Text",
        children: title
      }), /*#__PURE__*/_jsx(Markdown, {
        content: shortDescription,
        customRenderers: customRenderer(primaryForegroundColor),
        paragraphType: "medium",
        "data-id": "color-cards_index_ColorCardContent_Stack_Markdown"
      }), supplementaryText && /*#__PURE__*/_jsx(Text, {
        sx: {
          paddingY: 'spacing-md',
          color: secondaryForegroundColor,
          fontSize: 'font-sm'
        },
        lineHeight: "base",
        noOfLines: 3,
        "data-id": "color-cards_index_ColorCardContent_Text",
        children: supplementaryText
      })]
    }), callToAction && /*#__PURE__*/_jsx(Text, {
      sx: {
        color: primaryForegroundColor
      },
      "data-id": "color-cards_index_ColorCardContent_Text_4",
      children: /*#__PURE__*/_jsx(Link, {
        hasUnderline: true,
        href: callToAction.url,
        onClick: () => {
          if (clickEvent) {
            clickEvent(index !== null && index !== void 0 ? index : 0);
          }
          trackClickEvents.clicked(categories.COLOUR_CARD, labels.createTitleAndPosition(title, position !== null && position !== void 0 ? position : 1));
        },
        as: "a",
        "data-id": "color-cards_index_ColorCardContent_Text_Link",
        children: callToAction.label
      })
    }), tag && /*#__PURE__*/_jsx(Text, {
      sx: {
        paddingY: 'spacing-sm',
        fontSize: 'font-xs'
      },
      "data-id": "color-cards_index_ColorCardContent_Text",
      children: tag
    })]
  });
};
const customRenderer = textColor => () => ({
  // eslint-disable-next-line react/display-name
  paragraph: _ref2 => {
    let {
      children
    } = _ref2;
    return /*#__PURE__*/_jsx(Paragraph, {
      type: "medium",
      inlineStyles: {
        color: textColor
      },
      "data-id": "color-cards_index_ColorCardContent_Paragraph",
      children: children
    });
  }
});
export const ColorCards = _ref3 => {
  let {
    title,
    description,
    children,
    hasOuterMargins = true,
    enableH1Heading,
    as
  } = _ref3;
  const breakpoint = useBreakpoint(DEFAULT_BREAKPOINT);
  return /*#__PURE__*/_jsx(CoreContainer, {
    gutter: hasOuterMargins ? undefined : 'size-none',
    as: as,
    "data-id": "color-cards_index_ColorCardContent_CoreContainer",
    children: /*#__PURE__*/_jsxs(Stack, {
      gap: "spacing-sm",
      align: ['center', 'center', 'unset'],
      sx: hasOuterMargins ? {
        marginX: [null, null, null, '10%']
      } : undefined,
      "data-id": "color-cards_index_ColorCardContent_CoreContainer_Stack",
      children: [title || description ? /*#__PURE__*/_jsxs(Stack, {
        align: "center",
        "data-id": "color-cards_index_ColorCardContent_Stack_10",
        children: [title && /*#__PURE__*/_jsx(H3, {
          as: enableH1Heading ? 'h1' : 'h3',
          "data-id": "color-cards_index_ColorCardContent_H3",
          children: title
        }), description && /*#__PURE__*/_jsx(Text, {
          color: "quartz",
          align: "center",
          sx: {
            maxWidth: '60ch'
          },
          "data-id": "color-cards_index_ColorCardContent_Text",
          children: description
        })]
      }) : null, /*#__PURE__*/_jsx(Grid, {
        templateColumns: ['1fr', null, '1fr 1fr'],
        gap: "spacing-md",
        "data-id": "color-cards_index_ColorCardContent_Stack_Grid",
        children: React.Children.map(children, (child, index) => {
          if ( /*#__PURE__*/React.isValidElement(child)) {
            return /*#__PURE__*/React.cloneElement(child, {
              isFullWidth: React.Children.count(children) === 1 && ['2xl', 'xl', 'lg', 'md'].includes(breakpoint || DEFAULT_BREAKPOINT),
              sx: _objectSpread({}, React.Children.count(children) === 1 && ['2xl', 'xl', 'lg', 'md'].includes(breakpoint || DEFAULT_BREAKPOINT) ? _objectSpread(_objectSpread({}, child.props.sx ? child.props.sx : {}), {}, {
                gridColumn: ['span 1', null, 'span 2']
              }) : {}),
              position: index + 1
            });
          } else {
            return child;
          }
        })
      })]
    })
  });
};
export const ColorCard = forwardRef((_ref4, ref) => {
  let {
    isFullWidth = false,
    title,
    image,
    shortDescription,
    callToAction,
    supplementaryText,
    position,
    tag,
    sx,
    as,
    clickEvent
  } = _ref4;
  const defaultSupportingColor = useToken('colors', 'smoke');
  const squareImagePadding = useToken('space', 'spacing-md');
  const supportingColor = image.supportingColour || defaultSupportingColor;
  const {
    0: width,
    1: setWidth
  } = useState(() => 5 * squareImagePadding);
  useEffect(() => {
    function updateSize() {
      if (window) {
        setWidth(window.innerWidth);
      }
    }
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  const LinkOverlayAs = React.useMemo(() => asProps =>
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-has-content
  _jsx("a", _objectSpread(_objectSpread({}, asProps), {}, {
    onClick: () => {
      if (clickEvent) {
        clickEvent(position ? position - 1 : 0);
      }
      trackClickEvents.clicked(categories.COLOUR_CARD, labels.createTitleAndPosition(title, position !== null && position !== void 0 ? position : 1));
    },
    "data-id": "color-cards_index_ColorCardContent_a"
  })), [clickEvent, position, title]);
  return /*#__PURE__*/_jsxs(Stack, {
    ref: ref,
    sx: _objectSpread({
      backgroundColor: supportingColor,
      padding: 'spacing-md',
      maxWidth: ['456px', null, 'size-full'],
      minWidth: ["calc(".concat(width, "px - (5 * ").concat(squareImagePadding, "))"), '424px', 'auto']
    }, sx),
    as: as,
    gap: "spacing-md",
    shouldWrapChildren: false,
    direction: isFullWidth ? 'row' : 'column',
    "data-id": "color-cards_index_ColorCardContent_Stack",
    children: [callToAction ? /*#__PURE__*/_jsx(Stack, {
      sx: _objectSpread({
        position: 'relative'
      }, isFullWidth ? {
        flexBasis: '33%'
      } : {}),
      "data-id": "color-cards_index_ColorCardContent_Stack_16",
      children: /*#__PURE__*/_jsx(LinkOverlay, {
        href: callToAction.url,
        as: LinkOverlayAs,
        "data-id": "color-cards_index_ColorCardContent_Stack_LinkOverlay",
        children: /*#__PURE__*/_jsx(CloudinaryImage, {
          ratio: "1",
          imageSetOrImage: image,
          maxWidth: "456px",
          minWidth: ["calc(".concat(width, "px - (5 * ").concat(squareImagePadding, "))"), '424px', 'auto'],
          sizes: IMAGE_SIZES_COLOR_CARD_HERO,
          "data-id": "color-cards_index_ColorCardContent_LinkOverlay_CloudinaryImage"
        })
      })
    }) : /*#__PURE__*/_jsx(CloudinaryImage, {
      ratio: "1",
      imageSetOrImage: image,
      sx: _objectSpread({}, isFullWidth ? {
        flexBasis: '33%'
      } : {}),
      maxWidth: "456px",
      minWidth: ["calc(".concat(width, "px - (5 * ").concat(squareImagePadding, "))"), '424px', 'auto'],
      sizes: IMAGE_SIZES_COLOR_CARD_HERO,
      "data-id": "color-cards_index_ColorCardContent_CloudinaryImage_19"
    }), /*#__PURE__*/_jsx(Stack, {
      gap: "spacing-xs",
      justify: isFullWidth ? 'center' : 'space-between',
      sx: _objectSpread({}, isFullWidth ? {
        alignContent: 'center',
        textAlign: 'center',
        flexBasis: '70%'
      } : {
        flexGrow: 1,
        height: 'size-full'
      }),
      shouldWrapChildren: false,
      "data-id": "color-cards_index_ColorCardContent_Stack_Stack",
      children: /*#__PURE__*/_jsx(ColorCardContent, {
        isFullWidth: isFullWidth,
        title: title,
        shortDescription: shortDescription,
        callToAction: callToAction,
        supplementaryText: supplementaryText,
        supportingColor: supportingColor,
        position: position,
        tag: tag,
        clickEvent: () => {
          if (clickEvent) {
            clickEvent(position ? position - 1 : 0);
          }
        },
        "data-id": "color-cards_index_Stack_ColorCardContent"
      })
    })]
  });
});